/**
 * Etapa 6 — Documentos para assinatura digital.
 *
 * Escolha binaria entre COM documentos (marca Contrato como incluido) ou SEM
 * documentos. Quando "COM documentos" esta ativo, o gestor pode:
 *  - marcar/desmarcar cada tipo (Contrato / CCB / Nota Promissoria);
 *  - editar o template HTML com placeholders {{NOME}}, {{CPF}}, etc.;
 *  - trocar por um modelo pronto da biblioteca;
 *  - ver preview em tempo real com dados de exemplo.
 */

import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { PlanFormState } from "../types";
import {
  CONTRACT_LIBRARY,
  PLACEHOLDER_GROUPS,
  analyzeContractPlaceholders,
  renderContractPreview,
  templateForTipo,
  REQUIRED_PLACEHOLDERS,
} from "../contract-templates";
import { docxToHtml } from "../docx-to-html";
import type { ChargePlanDocumento, ChargePlanDocumentoTipo } from "../../../../services/charge-plans.service";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

type DocTipo = ChargePlanDocumentoTipo;

const TIPO_META: Record<DocTipo, { titulo: string; descricao: string; icon: string }> = {
  contrato: {
    titulo: "Contrato de prestacao de servicos",
    descricao: "Contrato geral com condicoes acordadas entre as partes.",
    icon: "📄",
  },
  ccb: {
    titulo: "CCB — Cedula de Credito Bancario",
    descricao: "Titulo executivo extrajudicial, forte respaldo judicial.",
    icon: "🏦",
  },
  nota_promissoria_digital: {
    titulo: "Nota Promissoria",
    descricao: "Modelo enxuto — valor total e prestacoes.",
    icon: "✍️",
  },
};

export function Step6Documents({ state, setState }: Props) {
  const optOut = state.documentos.every((d) => !d.incluir);
  const comDocumentos = !optOut;

  const aplicarOptOut = () =>
    setState((prev) => ({
      ...prev,
      documentos: prev.documentos.map((d) => ({ ...d, incluir: false })),
    }));

  const aplicarComDocumentos = () =>
    setState((prev) => ({
      ...prev,
      documentos: prev.documentos.map((d) =>
        d.tipo === "contrato" ? { ...d, incluir: true } : d,
      ),
    }));

  const toggleDocumento = (tipo: DocTipo) => {
    setState((prev) => ({
      ...prev,
      documentos: prev.documentos.map((d) =>
        d.tipo === tipo ? { ...d, incluir: !d.incluir } : d,
      ),
    }));
  };

  const updateDocumento = (tipo: DocTipo, patch: Partial<ChargePlanDocumento>) => {
    setState((prev) => ({
      ...prev,
      documentos: prev.documentos.map((d) => (d.tipo === tipo ? { ...d, ...patch } : d)),
    }));
  };

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          6. Documentos para assinatura digital
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure quais documentos serao enviados ao cliente e edite o
          template de cada um com placeholders.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <button
          type="button"
          onClick={() => {
            if (!comDocumentos) aplicarComDocumentos();
          }}
          className={`group relative flex flex-col rounded-2xl border-2 p-6 text-left shadow-sm transition ${
            comDocumentos
              ? "border-primary-500 bg-gradient-to-br from-primary-50 via-primary-50/60 to-card ring-2 ring-primary-200"
              : "border-primary-200 bg-gradient-to-br from-primary-50/40 via-card to-card hover:border-primary-400"
          }`}
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              <ShieldIcon />
              Recomendado
            </div>
            {comDocumentos ? (
              <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary-700">
                ✓ Selecionado
              </span>
            ) : null}
          </div>
          <h2 className="text-lg font-bold leading-tight text-foreground">
            Deixe sua cobrança{" "}
            <span className="text-primary-700">MAIS PROTEGIDA</span>
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Envie contratos, CCB ou notas promissórias com assinatura via
            WhatsApp e validade jurídica garantida pela lei brasileira.
          </p>
          <div
            className={`mt-5 flex h-11 items-center justify-center rounded-lg text-sm font-bold transition ${
              comDocumentos
                ? "bg-primary-600 text-white shadow-sm"
                : "border border-primary-300 bg-card text-primary-700 group-hover:bg-primary-50"
            }`}
          >
            {comDocumentos ? "✓ COM DOCUMENTOS" : "COM DOCUMENTOS"}
          </div>
        </button>

        <button
          type="button"
          onClick={() => {
            if (!optOut) aplicarOptOut();
          }}
          className={`group flex flex-col rounded-2xl border-2 p-6 text-left shadow-sm transition ${
            optOut
              ? "border-foreground bg-muted/40 ring-2 ring-foreground/10"
              : "border-border bg-card hover:border-foreground/40"
          }`}
        >
          <div className="mb-3 flex items-center gap-2">
            <div
              className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                optOut ? "bg-foreground text-card" : "bg-muted text-muted-foreground"
              }`}
            >
              Opção
            </div>
            {optOut ? (
              <span className="inline-flex w-fit items-center gap-1 rounded-full bg-foreground/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-foreground">
                ✓ Selecionado
              </span>
            ) : null}
          </div>
          <h2 className="text-lg font-bold leading-tight text-foreground">
            Não uso documentos por enquanto
          </h2>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
            Posso continuar sem utilizar documentos agora. Se precisar futuramente,
            nosso time está pronto para te ajudar.
          </p>
          <div
            className={`mt-5 flex h-11 items-center justify-center rounded-lg text-sm font-bold transition ${
              optOut
                ? "bg-foreground text-card shadow-sm"
                : "border border-input bg-card text-foreground group-hover:bg-muted"
            }`}
          >
            {optOut ? "✓ SEM DOCUMENTOS" : "SEM DOCUMENTOS"}
          </div>
        </button>
      </div>

      {comDocumentos && (
        <section className="flex flex-col gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">Documentos incluidos</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Marque quais serao enviados ao cliente para assinatura digital. Cada
              documento tem seu proprio template editavel.
            </p>
          </div>

          {state.documentos.map((doc) => (
            <DocumentoCard
              key={doc.tipo}
              doc={doc}
              onToggle={() => toggleDocumento(doc.tipo)}
              onUpdate={(patch) => updateDocumento(doc.tipo, patch)}
            />
          ))}
        </section>
      )}
    </div>
  );
}

function DocumentoCard({
  doc,
  onToggle,
  onUpdate,
}: {
  doc: ChargePlanDocumento;
  onToggle: () => void;
  onUpdate: (patch: Partial<ChargePlanDocumento>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [uploadState, setUploadState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ok"; fileName: string; warnings: string[] }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const templateRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const meta = TIPO_META[doc.tipo];
  const template = doc.templateHtml || templateForTipo(doc.tipo);
  const { unknown, missing } = analyzeContractPlaceholders(template);
  const hasIssues = unknown.length > 0 || missing.length > 0;

  const insertPlaceholder = (key: string) => {
    const ta = templateRef.current;
    const token = `{{${key}}}`;
    if (!ta) {
      onUpdate({ templateHtml: template + token });
      return;
    }
    const start = ta.selectionStart ?? template.length;
    const end = ta.selectionEnd ?? template.length;
    const next = template.slice(0, start) + token + template.slice(end);
    onUpdate({ templateHtml: next });
    // Reposiciona o cursor depois do token inserido.
    requestAnimationFrame(() => {
      const t = templateRef.current;
      if (!t) return;
      t.focus();
      const cursor = start + token.length;
      t.setSelectionRange(cursor, cursor);
    });
  };

  const handleDocxUpload = async (file: File) => {
    setUploadState({ kind: "loading" });
    try {
      const { html, messages } = await docxToHtml(file);
      onUpdate({ templateHtml: html });
      setUploadState({ kind: "ok", fileName: file.name, warnings: messages });
    } catch (err) {
      setUploadState({
        kind: "error",
        message: err instanceof Error ? err.message : "Falha ao converter arquivo.",
      });
    }
  };

  return (
    <div
      className={`rounded-2xl border-2 bg-card transition ${
        doc.incluir ? "border-primary-300" : "border-border"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3 p-4">
        <span className="text-2xl">{meta.icon}</span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-foreground">{meta.titulo}</h3>
          <p className="text-xs text-muted-foreground">{meta.descricao}</p>
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={doc.incluir}
            onChange={onToggle}
            className="h-5 w-5 cursor-pointer accent-primary-600"
          />
          <span className="text-xs font-medium text-foreground">
            {doc.incluir ? "Incluido" : "Nao incluir"}
          </span>
        </label>
      </div>

      {doc.incluir && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between text-sm font-semibold text-primary-700 hover:text-primary-800"
          >
            <span>
              {expanded ? "▼" : "▶"} Editar template do documento
              {hasIssues && !expanded && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                  {missing.length + unknown.length} avisos
                </span>
              )}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {template.length.toLocaleString()} caracteres
            </span>
          </button>

          {expanded && (
            <div className="mt-4 flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">
                    Titulo mostrado ao cliente
                  </label>
                  <input
                    type="text"
                    value={doc.tituloCliente ?? ""}
                    onChange={(e) => onUpdate({ tituloCliente: e.target.value })}
                    className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm"
                    placeholder="Ex: Contrato de prestacao de servicos"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">
                    Trocar por modelo pronto
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm"
                    value=""
                    onChange={(e) => {
                      const item = CONTRACT_LIBRARY.find((x) => x.id === e.target.value);
                      if (item) onUpdate({ templateHtml: item.html });
                    }}
                  >
                    <option value="">Selecionar um modelo…</option>
                    {CONTRACT_LIBRARY.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-foreground">
                  Mensagem que acompanha o documento
                </label>
                <textarea
                  value={doc.mensagem ?? ""}
                  onChange={(e) => onUpdate({ mensagem: e.target.value })}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Por favor, leia atentamente o documento antes de assinar."
                />
              </div>

              {/* Banner explicativo sobre variaveis */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs">
                <div className="mb-1 flex items-center gap-1.5 font-bold text-blue-900">
                  <InfoIcon /> Como funcionam as variaveis desta campanha
                </div>
                <p className="text-blue-900/90">
                  Ao enviar o contrato, cada variavel <code className="rounded bg-blue-100 px-1">{"{{...}}"}</code>{" "}
                  eh trocada pelo valor real da campanha e do cliente. Por exemplo,
                  <code className="mx-1 rounded bg-blue-100 px-1">{"{{JUROS}}"}</code> vira a taxa configurada nesta
                  campanha, <code className="mx-1 rounded bg-blue-100 px-1">{"{{VALOR_PARCELA}}"}</code> vira o valor
                  calculado do plano de parcelas e <code className="mx-1 rounded bg-blue-100 px-1">{"{{NOME}}"}</code>{" "}
                  vira o nome do cliente. Nao precisa preencher nada aqui.
                </p>
              </div>

              {/* Upload Word */}
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
                <span className="text-2xl">📄</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    Ja tem o contrato em Word?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Suba o arquivo <strong>.docx</strong> e ele substitui o texto atual. Depois voce insere as
                    variaveis onde precisar.
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleDocxUpload(f);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadState.kind === "loading"}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-60"
                >
                  {uploadState.kind === "loading" ? "Convertendo…" : "Subir .docx"}
                </button>
              </div>

              {uploadState.kind === "ok" && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                  ✓ <strong>{uploadState.fileName}</strong> importado. Revise abaixo e adicione as variaveis onde
                  precisar.
                  {uploadState.warnings.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer font-semibold">
                        {uploadState.warnings.length} aviso(s) da conversao
                      </summary>
                      <ul className="mt-1 list-disc pl-4">
                        {uploadState.warnings.slice(0, 5).map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              {uploadState.kind === "error" && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
                  ✗ {uploadState.message}
                </div>
              )}

              {/* Chips de variaveis */}
              <div>
                <p className="mb-1.5 text-xs font-semibold text-foreground">
                  Inserir variavel no texto (clique para adicionar onde o cursor esta)
                </p>
                <div className="flex flex-col gap-2">
                  {PLACEHOLDER_GROUPS.map((group) => (
                    <div key={group.titulo}>
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                        {group.titulo}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.chips.map((chip) => (
                          <button
                            key={chip.key}
                            type="button"
                            onClick={() => insertPlaceholder(chip.key)}
                            className="rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-800 transition hover:bg-primary-100"
                            title={`Insere {{${chip.key}}}`}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {hasIssues && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                  {missing.length > 0 && (
                    <p className="text-amber-900">
                      <strong>Variaveis recomendadas ausentes:</strong>{" "}
                      {missing.join(", ")}. Sugerido incluir para garantir dados minimos
                      no contrato ({REQUIRED_PLACEHOLDERS.join(", ")}).
                    </p>
                  )}
                  {unknown.length > 0 && (
                    <p className="mt-1 text-amber-900">
                      <strong>Variaveis nao reconhecidas:</strong>{" "}
                      {unknown.join(", ")}. Ficarao como texto literal no PDF final.
                    </p>
                  )}
                </div>
              )}

              {/* Editor + Preview */}
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">
                    Texto do documento
                  </label>
                  <textarea
                    ref={templateRef}
                    value={template}
                    onChange={(e) => onUpdate({ templateHtml: e.target.value })}
                    className={`h-96 w-full rounded-lg border border-input bg-card px-3 py-2 text-[12px] ${
                      showRawHtml ? "font-mono text-[11px]" : ""
                    }`}
                    spellCheck={false}
                  />
                  <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => setShowRawHtml((v) => !v)}
                      className="hover:text-foreground"
                    >
                      {showRawHtml ? "◀ Voltar para texto" : "Ver HTML avançado ▶"}
                    </button>
                    <span>{template.length.toLocaleString()} caracteres</span>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">
                    Preview com dados de exemplo
                  </label>
                  <div
                    className="h-96 overflow-auto rounded-lg border border-input bg-white p-3 text-black"
                    dangerouslySetInnerHTML={{ __html: renderContractPreview(template) }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
